/**
 * Thin, tool-agnostic product-analytics wrapper.
 *
 * PostHog lives ONLY behind this module, so the tool/host is swappable (e.g.
 * Cloud now, self-host later — just repoint the env vars) and the whole thing is
 * a NO-OP until `REACT_APP_POSTHOG_KEY` is set. Every call-site imports `track` /
 * `identifyUser` from here and nothing else.
 *
 * Privacy: no PII ever leaves the app —
 *  - user id is a pseudonymised (hashed) Zitadel `sub`, never name/email,
 *  - event props go through an allowlist-style sanitiser (no coords/emails/etc.),
 *  - autocapture, pageview capture, and session recording are OFF (manual events
 *    only, so we control exactly what is sent),
 *  - IP is dropped at ingestion (enable "Discard client IP data" in the PostHog
 *    project settings and/or reverse-proxy `REACT_APP_POSTHOG_HOST`).
 */

let posthog = null; // loaded lazily, only when enabled
let enabled = false;

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

// Canonical event names — import these instead of typing strings at call-sites.
export const EVENTS = {
  EXPLORE_OPENED: 'explore_opened',
  AOI_DEFINED: 'aoi_defined',
  CATALOG_SEARCHED: 'catalog_searched',
  SEARCH_RESULTS_RETURNED: 'search_results_returned',
  SEARCH_FAILED: 'search_failed',
  PRODUCT_EXPANDED: 'product_expanded',
  SCENE_SELECTED: 'scene_selected',
  CHECKOUT_OPENED: 'checkout_opened',
  BHOONIDHI_PORTAL_REDIRECT: 'bhoonidhi_portal_redirect',
  BHOONIDHI_CONNECT_ATTEMPTED: 'bhoonidhi_connect_attempted',
  BHOONIDHI_CONNECT_SUCCEEDED: 'bhoonidhi_connect_succeeded',
  BHOONIDHI_OTP_REQUIRED: 'bhoonidhi_otp_required',
};

// FNV-1a → hex. Turns the opaque Zitadel sub into a stable pseudonymous id (same
// user → same id, so retention/funnels work) without shipping the raw sub.
export const pseudonymize = (value) => {
  if (!value) return undefined;
  const s = String(value);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `u_${(h >>> 0).toString(16)}`;
};

// Bucket a raw AOI area (km²) so we never send an exact size (or coordinates).
export const areaBand = (sqKm) => {
  if (sqKm == null || Number.isNaN(sqKm)) return undefined;
  if (sqKm < 10) return '<10';
  if (sqKm < 50) return '10-50';
  if (sqKm < 200) return '50-200';
  if (sqKm < 1000) return '200-1k';
  if (sqKm < 10000) return '1k-10k';
  if (sqKm < 100000) return '10k-100k';
  return '100k+';
};

// Bucket a count so we never send exact selection/result sizes.
export const countBand = (n) => {
  if (n == null || Number.isNaN(n)) return undefined;
  if (n <= 0) return '0';
  if (n === 1) return '1';
  if (n <= 5) return '2-5';
  if (n <= 20) return '6-20';
  if (n <= 100) return '21-100';
  return '100+';
};

// Whole calendar months between two YYYY-MM-DD strings (privacy-safe span).
export const monthSpan = (startDate, endDate) => {
  if (!startDate || !endDate) return undefined;
  const s = new Date(startDate);
  const e = new Date(endDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return undefined;
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
};

// Banded age of the search window's END date — how recent the imagery they want
// is (recent vs historical demand). Not PII: it describes imagery dates, not the
// user.
export const dateRecency = (endDate) => {
  if (!endDate) return undefined;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return undefined;
  const days = (Date.now() - end.getTime()) / 86400000;
  if (days < 0) return 'future';
  if (days <= 7) return '<=7d';
  if (days <= 30) return '8-30d';
  if (days <= 90) return '1-3mo';
  if (days <= 365) return '3-12mo';
  if (days <= 1095) return '1-3y';
  return '>3y';
};

// Coarse AOI region: the AOI centroid snapped to a ~55 km (0.5°) grid cell,
// returned as the cell's SW corner "lat,lng". Deliberately imprecise — it tells
// you roughly WHERE demand is, at ~55 km resolution, WITHOUT storing the drawn
// AOI or precise coordinates. (This is the one intentional coarse-location prop;
// the drawn geometry / exact coords are never sent.)
export const regionCell = (coordinates, sizeDeg = 0.5) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return undefined;
  let latSum = 0;
  let lngSum = 0;
  let n = 0;
  for (const c of coordinates) {
    if (c == null || Number.isNaN(c.lat) || Number.isNaN(c.lng)) continue;
    latSum += c.lat;
    lngSum += c.lng;
    n += 1;
  }
  if (n === 0) return undefined;
  const snap = (v) => Math.floor(v / sizeDeg) * sizeDeg;
  return `${snap(latSum / n)},${snap(lngSum / n)}`;
};

// Guard against anything that could carry PII / precise location. Call-sites are
// already curated; this is a backstop.
const BLOCKED = /(email|name|password|token|secret|coord|latitude|longitude|\blat\b|\blng\b|username|address|phone)/i;
const sanitize = (props = {}) => {
  const out = {};
  Object.entries(props).forEach(([k, v]) => {
    if (BLOCKED.test(k) || v == null) return;
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') out[k] = v;
    else if (Array.isArray(v)) {
      out[k] = v.filter((x) => typeof x === 'string' || typeof x === 'number');
    }
  });
  return out;
};

export const initAnalytics = async () => {
  if (enabled || !KEY) return; // no-op until a key is configured
  try {
    const mod = await import('posthog-js');
    posthog = mod.default ?? mod.posthog ?? mod;
    posthog.init(KEY, {
      api_host: HOST,
      autocapture: false, // manual events only — full control over what is sent
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      persistence: 'localStorage+cookie',
    });
    enabled = true;
  } catch {
    /* analytics must never break the app */
  }
};

export const identifyUser = (sub, props = {}) => {
  if (!enabled || !posthog) return;
  const id = pseudonymize(sub);
  if (!id) return;
  try {
    posthog.identify(id, sanitize(props));
  } catch {
    /* noop */
  }
};

// Register super-properties — attached to EVERY subsequent event until reset.
// Used for the per-login `session_id` so a "flow" is scoped to one visit;
// `resetAnalytics()` (called on logout) clears these.
export const registerSuperProps = (props = {}) => {
  if (!enabled || !posthog) return;
  try {
    posthog.register(sanitize(props));
  } catch {
    /* noop */
  }
};

export const track = (event, props = {}) => {
  if (!enabled || !posthog || !event) return;
  try {
    posthog.capture(event, sanitize(props));
  } catch {
    /* noop */
  }
};

export const resetAnalytics = () => {
  if (!enabled || !posthog) return;
  try {
    posthog.reset();
  } catch {
    /* noop */
  }
};
